import React, { useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, MenuItem, Select, FormControl, InputLabel, Button, TextField,
  InputAdornment, Skeleton, IconButton, Tooltip, Drawer, Divider, Grid, useMediaQuery,
  Pagination, Snackbar,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useGetFinanceTransactionsQuery, useGetFinanceAccountsQuery } from "../../Api/attoDeskApi";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import { StudentProfileLink, ParentProfileLink } from "../../Components/ProfileLinks";
import LocationFilter, { useLocationFilter, toLocationParam } from "./LocationFilter";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/* ── Category palette (soft, premium) ─────────────────────────────── */
const CATEGORY_META: Record<string, { label: string; bg: string; fg: string }> = {
  payment:    { label: "Payment",     bg: "#dcfce7", fg: "#15803d" },
  refund:     { label: "Refund",      bg: "#fee2e2", fg: "#b91c1c" },
  expense:    { label: "Expense",     bg: "#ffedd5", fg: "#c2410c" },
  adjustment: { label: "Adjustment",  bg: "#ede9fe", fg: "#6d28d9" },
  credit_note:{ label: "Credit Note", bg: "#dbeafe", fg: "#1d4ed8" },
  transfer:   { label: "Transfer",    bg: "#e0f2fe", fg: "#0369a1" },
  opening:    { label: "Opening",     bg: "#f1f5f9", fg: "#475569" },
  other:      { label: "Other",       bg: "#f1f5f9", fg: "#475569" },
};

const catMeta = (c?: string) => CATEGORY_META[(c || "").toLowerCase()] || {
  label: c ? c.charAt(0).toUpperCase() + c.slice(1) : "—",
  bg: "#f1f5f9", fg: "#475569",
};

const money = (n: any) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── KPI card ─────────────────────────────────────────────────────── */
const KpiCard: React.FC<{ label: string; value: string; sub: string; icon: React.ReactNode; color: string; bg: string }> =
  ({ label, value, sub, icon, color, bg }) => (
  <Box sx={{
    bgcolor: "#fff", borderRadius: "16px",
    border: "1px solid rgba(15,23,42,0.06)",
    boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    p: 2.25, display: "flex", alignItems: "flex-start", gap: 1.75,
    transition: "box-shadow .18s", "&:hover": { boxShadow: "0 6px 18px rgba(15,23,42,0.06)" },
  }}>
    <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: bg, color, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 22 } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.15, mt: 0.25, letterSpacing: "-0.02em" }}>{value}</Typography>
      <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.25 }}>{sub}</Typography>
    </Box>
  </Box>
);

/* ── Category badge ───────────────────────────────────────────────── */
const CategoryBadge: React.FC<{ category?: string }> = ({ category }) => {
  const m = catMeta(category);
  return (
    <Chip label={m.label} size="small" sx={{
      height: 22, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.fg,
      textTransform: "none", border: 0,
    }} />
  );
};

/* ── Copy-able reference ──────────────────────────────────────────── */
const RefCopy: React.FC<{ value?: string; onCopied: () => void }> = ({ value, onCopied }) => {
  if (!value) return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, fontWeight: 600, color: "#334155", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", px: 0.75, py: 0.25 }}>
        {value}
      </Typography>
      <Tooltip title="Copy reference number" arrow>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value); onCopied(); }} sx={{ p: 0.25, color: "#94a3b8", "&:hover": { color: "#0f172a" } }}>
          <ContentCopyIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/* ── Description block ────────────────────────────────────────────── */
const DescriptionBlock: React.FC<{ tx: any }> = ({ tx }) => {
  const invoice = tx.invoice as { id?: number; invoiceNumber?: string } | null;
  const student = tx.student as { id?: number; name?: string } | null;
  const parent = tx.parent as { id?: number; name?: string } | null;
  const cat = (tx.category || "").toLowerCase();

  let title = tx.description || "Transaction";
  if (cat === "payment") title = "Payment Received";
  else if (cat === "refund") title = "Refund Issued";
  else if (cat === "expense") title = tx.expense?.title || "Expense";
  else if (cat === "adjustment") title = "Ledger Adjustment";
  else if (cat === "opening") title = "Opening Balance";

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>{title}</Typography>
      {invoice?.invoiceNumber && (
        <Typography sx={{ fontSize: 12, color: "#475569", mt: 0.15 }}>
          Invoice{" "}
          <Box component={RouterLink as any} to={`/finance/invoices/${invoice.id}`}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ color: "#1d4ed8", textDecoration: "none", fontWeight: 700,
              "&:hover": { textDecoration: "underline" } }}>
            {invoice.invoiceNumber}
          </Box>
        </Typography>
      )}
      {(student?.name || parent?.name) && (
        <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.15 }}>
          {student?.name && (
            <StudentProfileLink id={student.id} name={student.name} sx={{ color: "#334155", fontWeight: 600 }}>
              {student.name}
            </StudentProfileLink>
          )}
          {student?.name && parent?.name && <span> • </span>}
          {parent?.name && (
            <ParentProfileLink id={parent.id} name={parent.name} sx={{ color: "#334155", fontWeight: 600 }}>
              {parent.name}
            </ParentProfileLink>
          )}
        </Typography>
      )}
      {!invoice?.invoiceNumber && !student?.name && !parent?.name && tx.description && (
        <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.15 }}>{tx.description}</Typography>
      )}
    </Box>
  );
};

/* ── Detail drawer ────────────────────────────────────────────────── */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 1.1, borderBottom: "1px dashed #e2e8f0" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, minWidth: 130 }}>{label}</Typography>
    <Box sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right", minWidth: 0, wordBreak: "break-word" }}>{value ?? "—"}</Box>
  </Box>
);

const TransactionDrawer: React.FC<{ tx: any | null; onClose: () => void }> = ({ tx, onClose }) => {
  const isCredit = tx?.type === "credit";
  return (
    <Drawer anchor="right" open={!!tx} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, bgcolor: "#fff" } }}>
      {tx && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ p: 2.5, borderBottom: "1px solid #eef2f7", background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Transaction</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", mt: 0.25 }}>
                  {isCredit ? "+" : "−"}{money(tx.amount)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                  <CategoryBadge category={tx.category} />
                  <Chip label={isCredit ? "Credit" : "Debit"} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: isCredit ? "#dcfce7" : "#fee2e2", color: isCredit ? "#15803d" : "#b91c1c" }} />
                </Box>
              </Box>
              <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
            <DetailRow label="Date & Time" value={tx.transaction_date ? dayjs(tx.transaction_date).format("MMM D, YYYY") + (tx.created_at ? " • " + dayjs(tx.created_at).format("h:mm A") : "") : "—"} />
            <DetailRow label="Reference" value={<span style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{tx.reference_no || tx.reference || "—"}</span>} />
            <DetailRow label="Account" value={tx.account?.name || "—"} />
            <DetailRow label="Payment Method" value={tx.payment_method || "—"} />
            {tx.invoice?.invoiceNumber && (
              <DetailRow label="Invoice" value={
                <Box component={RouterLink as any} to={`/finance/invoices/${tx.invoice.id}`} onClick={onClose}
                  sx={{ color: "#1d4ed8", textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                  {tx.invoice.invoiceNumber}
                </Box>
              } />
            )}
            {tx.student?.name && (
              <DetailRow label="Student" value={
                <StudentProfileLink id={tx.student.id} name={tx.student.name} sx={{ color: "#0f172a" }}>{tx.student.name}</StudentProfileLink>
              } />
            )}
            {tx.parent?.name && (
              <DetailRow label="Parent" value={
                <ParentProfileLink id={tx.parent.id} name={tx.parent.name} sx={{ color: "#0f172a" }}>{tx.parent.name}</ParentProfileLink>
              } />
            )}
            <DetailRow label="Running Balance" value={money(tx.balance_after)} />
            <DetailRow label="Description" value={tx.description || "—"} />
            {tx.expense?.vendor && <DetailRow label="Vendor" value={tx.expense.vendor} />}

            {tx.invoice && tx.payment && (
              <Box sx={{ mt: 3, p: 2, bgcolor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>Payment Allocation</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, fontSize: 12.5 }}>
                  <span>Invoice Total</span><strong>{money(tx.invoice.totalAmount)}</strong>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, fontSize: 12.5 }}>
                  <span>Paid this transaction</span><strong>{money(tx.payment.amount)}</strong>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, fontSize: 12.5 }}>
                  <span>Cumulative paid</span><strong>{money(tx.invoice.paid_amount)}</strong>
                </Box>
                <Divider sx={{ my: 0.75 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, fontSize: 13, fontWeight: 800 }}>
                  <span>Remaining balance</span>
                  <span style={{ color: Number(tx.invoice.balance_due) > 0 ? "#b91c1c" : "#15803d" }}>{money(tx.invoice.balance_due)}</span>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

/* ── The page ─────────────────────────────────────────────────────── */
const Transactions = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall  = useMediaQuery(theme.breakpoints.down("sm"));

  const [accountId, setAccountId] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [txType, setTxType] = useState<string>(""); // all/credit/debit/payment/refund/expense/adjustment
  const [method, setMethod] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [searchApplied, setSearchApplied] = useState<string>("");
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [detailTx, setDetailTx] = useState<any | null>(null);
  const [copiedOpen, setCopiedOpen] = useState(false);

  const { locationId } = useLocationFilter();

  const { data: accData } = useGetFinanceAccountsQuery();
  const accounts: any[] = useMemo(() => Array.isArray(accData?.data) ? accData!.data as any[] : [], [accData]);

  const params: any = { startDate, endDate, ...toLocationParam(locationId) };
  if (accountId) params.account_id = Number(accountId);
  if (category) params.category = category;
  if (txType === "credit" || txType === "debit") params.type = txType;
  else if (["payment", "refund", "expense", "adjustment"].includes(txType)) params.category = txType;
  if (method) params.payment_method = method;
  if (searchApplied) params.search = searchApplied;

  const { data: txData, isLoading, isError, refetch } = useGetFinanceTransactionsQuery(params);
  const txResult = useMemo(() => (txData?.data as any) || {}, [txData]);
  const transactions: any[] = txResult.transactions || [];
  const summary = txResult.summary || {};

  const applyFilters = useCallback(() => {
    setSearchApplied(search.trim());
    setPage(1);
  }, [search]);

  const clearFilters = useCallback(() => {
    setAccountId(""); setCategory(""); setTxType(""); setMethod("");
    setSearch(""); setSearchApplied("");
    setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
    setEndDate(dayjs().endOf("month").format("YYYY-MM-DD"));
    setPage(1);
  }, []);

  const total = transactions.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const rangeStart = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, total);
  const pageRows = transactions.slice(rangeStart - 1, rangeEnd);

  /* ── Export CSV ── */
  const exportCsv = () => {
    const headers = ["Date", "Reference", "Description", "Student", "Parent", "Invoice", "Account", "Category", "Payment Method", "Credit", "Debit", "Balance", "Location"];
    const rows = transactions.map((t: any) => [
      t.transaction_date ? dayjs(t.transaction_date).format("YYYY-MM-DD") : "",
      t.reference_no || "",
      (t.description || "").replace(/\n/g, " "),
      t.student?.name || "",
      t.parent?.name || "",
      t.invoice?.invoiceNumber || "",
      t.account?.name || "",
      t.category || "",
      t.payment_method || "",
      t.type === "credit" ? Number(t.amount).toFixed(2) : "",
      t.type === "debit" ? Number(t.amount).toFixed(2) : "",
      Number(t.balance_after || 0).toFixed(2),
      t.location_id || "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transactions_${startDate}_${endDate}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ── Print report ── */
  const printReport = () => {
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    const rowsHtml = transactions.map((t: any) => `
      <tr>
        <td>${t.transaction_date ? dayjs(t.transaction_date).format("MMM D, YYYY") : ""}</td>
        <td>${t.reference_no || ""}</td>
        <td>${(t.description || "").replace(/</g, "&lt;")} ${t.invoice?.invoiceNumber ? "<br><small>Invoice " + t.invoice.invoiceNumber + "</small>" : ""}</td>
        <td>${t.student?.name || ""}${t.parent?.name ? " / " + t.parent.name : ""}</td>
        <td>${(t.category || "").toString()}</td>
        <td>${t.payment_method || ""}</td>
        <td style="text-align:right;color:#15803d">${t.type === "credit" ? money(t.amount) : ""}</td>
        <td style="text-align:right;color:#b91c1c">${t.type === "debit" ? money(t.amount) : ""}</td>
        <td style="text-align:right"><strong>${money(t.balance_after)}</strong></td>
      </tr>`).join("");
    const activeFilters: string[] = [];
    if (accountId) activeFilters.push("Account: " + (accounts.find(a => String(a.id) === accountId)?.name || accountId));
    if (category) activeFilters.push("Category: " + category);
    if (txType) activeFilters.push("Type: " + txType);
    if (method) activeFilters.push("Method: " + method);
    if (searchApplied) activeFilters.push("Search: " + searchApplied);
    w.document.write(`
      <html><head><title>Transactions Report</title>
      <style>
        body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color:#0f172a; padding:24px; }
        h1 { margin:0 0 4px; font-size:20px; }
        .brand { font-size:11px; color:#64748b; letter-spacing:.08em; text-transform:uppercase; }
        .meta { font-size:12px; color:#475569; margin:8px 0 16px; }
        .kpis { display:flex; gap:12px; margin:14px 0 18px; }
        .kpi { flex:1; border:1px solid #e2e8f0; border-radius:10px; padding:10px 12px; }
        .kpi small { font-size:10px; color:#64748b; letter-spacing:.06em; text-transform:uppercase; }
        .kpi div { font-size:16px; font-weight:800; margin-top:2px; }
        table { width:100%; border-collapse:collapse; font-size:11.5px; }
        th, td { padding:6px 8px; border-bottom:1px solid #e2e8f0; text-align:left; vertical-align:top; }
        th { background:#f8fafc; font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; color:#475569; }
        .foot { margin-top:16px; font-size:10.5px; color:#94a3b8; }
      </style></head><body>
        <div class="brand">Finance</div>
        <h1>Transactions Report</h1>
        <div class="meta">Period: ${dayjs(startDate).format("MMM D, YYYY")} — ${dayjs(endDate).format("MMM D, YYYY")}${activeFilters.length ? " • " + activeFilters.join(" • ") : ""}</div>
        <div class="kpis">
          <div class="kpi"><small>Total Credits</small><div style="color:#15803d">${money(summary.totalCredits)}</div></div>
          <div class="kpi"><small>Total Debits</small><div style="color:#b91c1c">${money(summary.totalDebits)}</div></div>
          <div class="kpi"><small>Net Balance</small><div>${money(summary.net)}</div></div>
          <div class="kpi"><small>Transactions</small><div>${transactions.length}</div></div>
        </div>
        <table><thead><tr>
          <th>Date</th><th>Reference</th><th>Description</th><th>Student / Parent</th>
          <th>Category</th><th>Method</th><th style="text-align:right">Credit</th>
          <th style="text-align:right">Debit</th><th style="text-align:right">Balance</th>
        </tr></thead><tbody>${rowsHtml}</tbody></table>
        <div class="foot">Generated ${dayjs().format("MMM D, YYYY h:mm A")}</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100%", pb: 4 }}>
      {/* Premium header */}
      <Box sx={{
        borderRadius: "18px",
        background: "linear-gradient(135deg, #f0f6ff 0%, #ffffff 60%)",
        border: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        p: { xs: 2.25, md: 3 }, mb: 2.5,
        display: "flex", justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" }, gap: 2,
      }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.1em" }}>Finance</Typography>
          <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a", mt: 0.25 }}>Transactions</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5, maxWidth: 640 }}>
            Review payments, refunds, expenses, adjustments, and account activity.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <LocationFilter />
          <Button onClick={exportCsv} startIcon={<FileDownloadIcon />} size="small" variant="outlined"
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: "#cbd5e1", color: "#0f172a", bgcolor: "#fff" }}>
            Export CSV
          </Button>
          <Button onClick={printReport} startIcon={<PrintIcon />} size="small" variant="outlined"
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: "#cbd5e1", color: "#0f172a", bgcolor: "#fff" }}>
            Print Report
          </Button>
        </Box>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? <Skeleton variant="rounded" height={92} sx={{ borderRadius: "16px" }} /> :
            <KpiCard label="Total Credits" value={money(summary.totalCredits)} sub="Payments received"
              icon={<TrendingUpIcon />} color="#15803d" bg="#dcfce7" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? <Skeleton variant="rounded" height={92} sx={{ borderRadius: "16px" }} /> :
            <KpiCard label="Total Debits" value={money(summary.totalDebits)} sub="Refunds and expenses"
              icon={<TrendingDownIcon />} color="#b91c1c" bg="#fee2e2" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? <Skeleton variant="rounded" height={92} sx={{ borderRadius: "16px" }} /> :
            <KpiCard label="Net Balance" value={money(summary.net)} sub="Credits minus debits"
              icon={<AccountBalanceWalletIcon />} color="#1d4ed8" bg="#dbeafe" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? <Skeleton variant="rounded" height={92} sx={{ borderRadius: "16px" }} /> :
            <KpiCard label="Transactions" value={String(summary.count ?? transactions.length ?? 0)} sub="Within selected period"
              icon={<ReceiptIcon />} color="#7c3aed" bg="#ede9fe" />}
        </Grid>
      </Grid>

      {/* Filters card */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 1px 3px rgba(15,23,42,0.04)", mb: 2.5, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f7", display: "flex", alignItems: "center", gap: 1 }}>
          <FilterAltIcon sx={{ fontSize: 18, color: "#1d4ed8" }} />
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a" }}>Filters</Typography>
            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Filter transactions by date, account, category, type, or reference.</Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <AppDatePicker size="small" fullWidth label="From" value={startDate} onChange={setStartDate}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13 } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDatePicker size="small" fullWidth label="To" value={endDate} onChange={setEndDate}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13 } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Account</InputLabel>
                <Select value={accountId} onChange={(e) => setAccountId(e.target.value as string)} label="Account" sx={{ borderRadius: "10px", fontSize: 13 }}>
                  <MenuItem value="">All Accounts</MenuItem>
                  {accounts.map((a: any) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={category} onChange={(e) => setCategory(e.target.value as string)} label="Category" sx={{ borderRadius: "10px", fontSize: 13 }}>
                  <MenuItem value="">All Categories</MenuItem>
                  {["payment", "refund", "expense", "adjustment", "transfer", "opening"].map(c => (
                    <MenuItem key={c} value={c} sx={{ textTransform: "capitalize" }}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select value={txType} onChange={(e) => setTxType(e.target.value as string)} label="Transaction Type" sx={{ borderRadius: "10px", fontSize: 13 }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="refund">Refund</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="adjustment">Adjustment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select value={method} onChange={(e) => setMethod(e.target.value as string)} label="Payment Method" sx={{ borderRadius: "10px", fontSize: 13 }}>
                  <MenuItem value="">All Methods</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField size="small" fullWidth placeholder="Search reference, invoice, student, parent, description, method…"
                value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                  sx: { borderRadius: "10px", fontSize: 13 } }} />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button size="small" variant="text" onClick={clearFilters}
              sx={{ textTransform: "none", fontWeight: 700, color: "#475569" }}>
              Clear Filters
            </Button>
            <Button size="small" variant="contained" onClick={applyFilters}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", boxShadow: "none", bgcolor: "#1d4ed8", "&:hover": { bgcolor: "#1e40af" } }}>
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table / cards */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 1px 3px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} height={54} sx={{ mb: 0.5, borderRadius: "8px" }} />)}
          </Box>
        ) : isError ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ErrorOutlineIcon sx={{ fontSize: 40, color: "#fca5a5", mb: 1 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Unable to load transactions</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.5 }}>Please retry in a moment.</Typography>
            <Button onClick={() => refetch()} variant="contained" size="small"
              sx={{ mt: 2, textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "#1d4ed8" }}>
              Retry
            </Button>
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 7 }}>
            <ReceiptLongIcon sx={{ fontSize: 44, color: "rgba(15,23,42,0.12)", mb: 1 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>No transactions found</Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.5 }}>Try changing the date range or clearing the selected filters.</Typography>
            <Button onClick={clearFilters} variant="outlined" size="small"
              sx={{ mt: 2, textTransform: "none", borderRadius: "10px", borderColor: "#cbd5e1", color: "#0f172a" }}>
              Clear Filters
            </Button>
          </Box>
        ) : isSmall ? (
          /* Mobile cards */
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
            {pageRows.map((tx: any) => {
              const isCredit = tx.type === "credit";
              return (
                <Box key={tx.id} onClick={() => setDetailTx(tx)}
                  sx={{ p: 1.75, border: "1px solid #eef2f7", borderRadius: "12px", cursor: "pointer",
                    "&:hover": { borderColor: "#cbd5e1", bgcolor: "#fafbfd" } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>{dayjs(tx.transaction_date).format("MMM D, YYYY")}</Typography>
                    <CategoryBadge category={tx.category} />
                  </Box>
                  <DescriptionBlock tx={tx} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: isCredit ? "#15803d" : "#b91c1c" }}>
                      {isCredit ? "+" : "−"}{money(tx.amount)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: Number(tx.balance_after) < 0 ? "#b91c1c" : "#0f172a", fontWeight: 700 }}>
                      Bal {money(tx.balance_after)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ "& td, & th": { borderBottom: "1px solid #f1f5f9" } }}>
              <TableHead sx={{ position: "sticky", top: 0, bgcolor: "#f8fafc", zIndex: 1 }}>
                <TableRow>
                  {[
                    { k: "date",    label: "Date" },
                    { k: "ref",     label: "Reference" },
                    { k: "desc",    label: "Description" },
                    { k: "sp",      label: "Student / Parent", hide: isMobile },
                    { k: "inv",     label: "Invoice", hide: isMobile },
                    { k: "method",  label: "Method", hide: isMobile },
                    { k: "cat",     label: "Category" },
                    { k: "credit",  label: "Credit", align: "right" as const },
                    { k: "debit",   label: "Debit",  align: "right" as const },
                    { k: "bal",     label: "Balance", align: "right" as const },
                    { k: "act",     label: "" },
                  ].filter(c => !c.hide).map(c => (
                    <TableCell key={c.k} align={c.align || "left"} sx={{ fontSize: 10.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", py: 1.25, bgcolor: "#f8fafc" }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((tx: any) => {
                  const isCredit = tx.type === "credit";
                  const bal = Number(tx.balance_after || 0);
                  return (
                    <TableRow key={tx.id} hover onClick={() => setDetailTx(tx)}
                      sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12.5, color: "#0f172a", whiteSpace: "nowrap" }}>
                        {dayjs(tx.transaction_date).format("MMM D, YYYY")}
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <RefCopy value={tx.reference_no} onCopied={() => setCopiedOpen(true)} />
                      </TableCell>
                      <TableCell sx={{ py: 1, maxWidth: 320 }}>
                        <DescriptionBlock tx={tx} />
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontSize: 12, py: 1 }}>
                          {tx.student?.name && (
                            <div>
                              <StudentProfileLink id={tx.student.id} name={tx.student.name} sx={{ color: "#0f172a" }}>
                                {tx.student.name}
                              </StudentProfileLink>
                            </div>
                          )}
                          {tx.parent?.name && (
                            <div style={{ color: "#64748b", fontSize: 11.5 }}>
                              <ParentProfileLink id={tx.parent.id} name={tx.parent.name} sx={{ color: "#64748b" }}>
                                {tx.parent.name}
                              </ParentProfileLink>
                            </div>
                          )}
                          {!tx.student?.name && !tx.parent?.name && <span style={{ color: "#cbd5e1" }}>—</span>}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell sx={{ py: 1 }}>
                          {tx.invoice?.invoiceNumber ? (
                            <Box component={RouterLink as any} to={`/finance/invoices/${tx.invoice.id}`}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              sx={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                              {tx.invoice.invoiceNumber}
                            </Box>
                          ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell sx={{ fontSize: 12, color: "#475569", textTransform: "capitalize", py: 1 }}>
                          {tx.payment_method ? String(tx.payment_method).replace(/_/g, " ") : "—"}
                        </TableCell>
                      )}
                      <TableCell sx={{ py: 1 }}>
                        <CategoryBadge category={tx.category} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, color: "#15803d", whiteSpace: "nowrap", py: 1, width: 120 }}>
                        {isCredit && (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
                            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                            {money(tx.amount)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, color: "#b91c1c", whiteSpace: "nowrap", py: 1, width: 120 }}>
                        {!isCredit && (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
                            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                            {money(tx.amount)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 800, color: bal < 0 ? "#b91c1c" : "#0f172a", whiteSpace: "nowrap", py: 1, width: 130 }}>
                        {money(bal)}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, width: 100 }}>
                        <Tooltip title="View Transaction" arrow>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailTx(tx); }}>
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {tx.invoice?.id && (
                          <Tooltip title="View Invoice" arrow>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/finance/invoices/${tx.invoice.id}`); }}>
                              <ReceiptLongIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination footer */}
        {!isLoading && !isError && transactions.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderTop: "1px solid #eef2f7", bgcolor: "#fbfcfe" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>Rows per page</Typography>
              <Select size="small" value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                sx={{ fontSize: 12, height: 30, "& .MuiSelect-select": { py: 0.5 } }}>
                {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
              <Typography sx={{ fontSize: 12, color: "#64748b", ml: 1 }}>
                Showing {rangeStart}–{rangeEnd} of {total} transactions
              </Typography>
            </Box>
            <Pagination
              size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded"
              sx={{ "& .MuiPaginationItem-root": { fontWeight: 700, fontSize: 12 } }}
            />
          </Box>
        )}
      </Box>

      <TransactionDrawer tx={detailTx} onClose={() => setDetailTx(null)} />
      <Snackbar open={copiedOpen} autoHideDuration={1600} onClose={() => setCopiedOpen(false)} message="Reference copied" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
};

export default Transactions;
